import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, Phone, Mail, MapPin, Lock, UserPlus, ChevronDown } from 'lucide-react';
import axios from 'axios';

/* =========================
   UTILITÁRIOS DE TELEFONE
========================= */
const onlyNumbers = (value = '') => value.replace(/\D/g, '');

const formatPhoneBR = (value = '') => {
  const numbers = onlyNumbers(value);

  if (numbers.length <= 10) {
    return numbers.replace(
      /(\d{2})(\d{4})(\d{0,4})/,
      '($1) $2-$3'
    );
  }

  return numbers.replace(
    /(\d{2})(\d{5})(\d{0,4})/,
    '($1) $2-$3'
  );
};

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '', // sempre SEM máscara
    email: '',
    city: '',
    user_type: 'consumidor',
    role: 'user',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const { login, API, siteSettings } = useContext(AuthContext);
  const navigate = useNavigate();

  /* ===== AUTOCOMPLETE CIDADES ===== */
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await fetch(`${API}/cities`);
      if (response.ok) {
        const data = await response.json();
        setCities(data);
        setFilteredCities(data.slice(0, 10));
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const searchCities = (term) => {
    if (!term) return cities.slice(0, 10);
    const t = term.toLowerCase();
    return cities.filter(c =>
      c.name.toLowerCase().includes(t) ||
      c.state.toLowerCase().includes(t) ||
      c.full_name.toLowerCase().includes(t)
    ).slice(0, 10);
  };

  /* ===== SUBMIT ===== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        phone: onlyNumbers(formData.phone) // 🔥 GARANTIA FINAL
      };

      const response = await axios.post(`${API}/auth/register`, payload);
      const { access_token, user } = response.data;

      login(access_token, user);
      toast.success('Conta criada com sucesso!');

      navigate(user.user_type === 'produtor' ? '/produtor' : '/marketplace');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  /* ===== INPUTS ===== */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      setFormData(prev => ({
        ...prev,
        phone: onlyNumbers(value) // salva SEM máscara
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /* ===== CIDADES ===== */
  const handleCitySearch = (e) => {
    const value = e.target.value;
    setCitySearch(value);
    setShowCityDropdown(true);
    setFilteredCities(searchCities(value));
  };

  const handleCitySelect = (city) => {
    setFormData(prev => ({ ...prev, city: city.full_name }));
    setCitySearch(city.full_name);
    setShowCityDropdown(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-green-600">Cadastrar</h2>
          <p className="text-gray-600">Crie sua conta na {siteSettings?.site_name || 'Nossa Feira'}</p>
        </div>

        <Card className="card-feira">
          <CardHeader className="text-center">
            <CardTitle className="flex justify-center gap-2 text-green-600">
              <UserPlus /> Criar Conta
            </CardTitle>
            <CardDescription>
              Junte-se à comunidade local
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nome */}
              <Label>Nome Completo</Label>
              <Input
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
              />

              {/* Telefone */}
              <Label>Telefone / WhatsApp</Label>
              <Input
                name="phone"
                required
                placeholder="(77) 9 9999-9999"
                value={formatPhoneBR(formData.phone)} // 👁️ VISUAL
                onChange={handleChange}
              />

              {/* Email */}
              <Label>E-mail</Label>
              <Input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
              />

              {/* Cidade */}
              <Label>Cidade</Label>
              <div className="relative">
                <Input
                  value={citySearch || formData.city}
                  onChange={handleCitySearch}
                  onFocus={() => setShowCityDropdown(true)}
                />
                {showCityDropdown && (
                  <div className="absolute z-50 bg-white border rounded w-full">
                    {filteredCities.map((c, i) => (
                      <div
                        key={i}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleCitySelect(c)}
                      >
                        {c.full_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Senha */}
              <Label>Senha</Label>
              <Input
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Criando conta...' : 'Criar Conta'}
              </Button>
            </form>

            <p className="text-center text-sm mt-4">
              Já tem conta? <Link to="/login" className="text-green-600 underline">Login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
